import * as DiscoverInterfaces from '../../../Interfaces/discover.interfaces';
import * as Fields from '../../Atoms/Fields';
import * as discoverRoutines from '../../../Containers/Discover/discoverRoutines';
import {backgroundOptions, getSupportedContentTypes} from './supportedContentTypes';
import {isEmpty} from '../../../helpers/isEmpty';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from 'react-redux';
import Button from '@material-ui/core/Button';
import DiscoverCardTypeInfo from './DiscoverCardTypeInfo';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import React, {useEffect} from 'react';
import Typography from '@material-ui/core/Typography';
import Validator from '../../../helpers/validator';
import discoverFormValidationRules from './DiscoverFormValidationRules';
import payloadAssembler from './payloadAssembler';
import useForm from './useForm';
import DiscoverCardTypeLinkAds from "./DiscoverCardTypeLinkAds";
import config from "../../../globals/config";
import {getIsBetaEnabled} from "../../../Containers/Events/selectors";

const localStyles = makeStyles(theme => ({
    button: {
        color: theme.palette.common.white,
        marginTop: theme.spacing(3),
    },
    card: {
        marginBottom: theme.spacing(3),
    },
    detailsHeadings: {},
    divider: {
        marginBottom: theme.spacing(5),
        marginTop: theme.spacing(1),
    },
    form: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        padding: theme.spacing(3),
    },
    removeButton: {
        backgroundColor: 'red',
        color: theme.palette.common.white,
        marginTop: theme.spacing(3),
    },
    row: {
        display: 'flex',
    },
}));

interface IDiscoverDetailsForm {
    activeDiscover: DiscoverInterfaces.IDiscoverNode;
    updateDiscover: Function;
    removeDiscover: Function;
    removeDocument: Function;
    removeAd:any;
    discoverCardStatus: DiscoverInterfaces.IDiscoverCardStatus;
    sitePages: any[];
}

const DiscoverDetailsForm = ({
                                 activeDiscover,
                                 discoverCardStatus,
                                 removeDocument,
    removeAd,
                                 sitePages,
                                 updateDiscover,
                             }: IDiscoverDetailsForm) => {
    const onSubmit = (values: DiscoverInterfaces.IDiscoverDetailsValues) => {
        updateDiscover(payloadAssembler(values));
    };
    const {errors, handleChange, handleSubmit, setValues, values} = useForm(
        onSubmit,
        Validator(discoverFormValidationRules),
    );
    const dispatch = useDispatch();
    const isBetaEnabled  = useSelector(getIsBetaEnabled)

    const handleAdUrlChange = (val:any, adId:string) =>{
        setValues({...values, adUrls:{...values['adUrls'], [adId]:val}})
    }
    // This updates our form's state when details of the activeDiscover come in
    // asynchronously
    useEffect(() => {
        let cardBackground;
        if (activeDiscover && activeDiscover.content) {
            //Set initial values for details
            const {
                cardImageURL,
                content,
                headerName,
                index,
                isNameHidden,
                isAd,
                isSmallSize,
                name,
                shouldUseCommonBackgroundImage,
            } = activeDiscover;

            if (shouldUseCommonBackgroundImage) {
                cardBackground = 'global';
            } else if (isEmpty(cardImageURL)) {
                cardBackground = 'none';
            } else {
                cardBackground = 'custom';
            }
            //Set the values in our hook based off of the activeDiscover node from firebase/our selector
            setValues({
                // ...values,
                cardBackground,
                cardImageURL,
                headerName,
                index,
                isAd,
                isNameHidden,
                isSmallSize,
                name,
                ads: content.ads,

                documents: content.documents,
                ...content,
            });
        }
        // eslint-disable-next-line
    }, [activeDiscover]);

    //Unmounting/cleanup
    useEffect(() => {
        return () => {
            dispatch(discoverRoutines.setDiscoverCardStatus('closed'));
        };
    }, []);

    const classes = localStyles();

    const types = getSupportedContentTypes(isBetaEnabled);
    const contentFields = (type: string) => {
        switch (type) {
            case 'list':
                return (
                    <Fields.FieldToggleSwitch
                        hook={{
                            errors,
                            values,
                            handleChange,
                        }}
                        label="Searchable List?"
                        name="isSearchable"
                    />
                );
            case 'info':
                return (
                    <DiscoverCardTypeInfo
                        activeDiscover={activeDiscover}
                        discoverCardStatus={discoverCardStatus}
                        handleSubmit={handleSubmit}
                        hook={{
                            errors,
                            values,
                            handleChange,
                        }}
                        removeDocument={removeDocument}
                    />
                );

            case 'link':
                if(values['isAd']){
                    return <DiscoverCardTypeLinkAds
                        handleAdUrlChange={handleAdUrlChange}
                        activeDiscover={activeDiscover}
                        discoverCardStatus={discoverCardStatus}
                        handleSubmit={handleSubmit}
                        hook={{
                            errors,
                            values,
                            handleChange,
                        }}
                        removeAd={removeAd}
                     />
                }else{
                    return (
                        <Fields.TypeLinkFields
                            hook={{
                                errors,
                                values,
                                handleChange,
                            }}
                        />
                    );
                }


            case 'webview':
                return (
                    <Fields.TypeLinkFields
                        hook={{
                            errors,
                            values,
                            handleChange,
                        }}
                    />
                );

            case 'scrollableText':
                return (
                    <Fields.TypeScrollableTextFields
                        hook={{
                            values,
                            errors,
                            handleChange,
                        }}
                        name="scrollableText"
                    />
                );
            case 'html':
                return (
                    <Fields.TypeHTMLFields
                        hook={{
                            values,
                            errors,
                            handleChange,
                        }}
                    />
                );

            case 'map':
                return (
                    <Fields.TypeMapFields
                        hook={{
                            values,
                            errors,
                            handleChange,
                        }}
                        name="address"
                    />
                );

            case 'zoomImage':
                return (
                    <Fields.TypeImageField
                        hook={{
                            values,
                            errors,
                            handleChange,
                        }}
                        labels={[
                            {
                                type: '.jpg, .png, .gif',
                                additional: {
                                    file: 'Limit: < 5mb',
                                    dimensions: 'Optimal: 1024px x 426px',
                                },
                                icon: 'photo',
                            },
                        ]}
                        name="imageURL"
                    />
                );
            case 'canvas':
                return (
                    <Fields.FieldTypeSelector
                        hook={{
                            values,
                            errors,
                            handleChange,
                        }}
                        label="Site page"
                        name="sitePageId"
                        options={sitePages}
                    />
                );
        }
    };

    const AppearanceDetails = () => {
        return (
            <React.Fragment>
                <Typography align="left" className={classes.detailsHeadings} variant="h6">
                    Appearance
                </Typography>
                <Divider className={classes.divider}/>
                <Grid justify="flex-start" container>

                    {(config.REACT_APP_PLATFORM == 'ef' && values['type'] === 'link') &&
                            <Fields.TypeToggleFields
                                hook={{
                                    values,
                                    errors,
                                    handleChange,
                                }}
                                label={'Is ad?'}
                                name={'isAd'}
                            />
                    }
                </Grid>
                <Grid alignItems="center" container>
                    <Grid item xs>
                        <Fields.TypeNameFields
                            hook={{
                                values,
                                errors,
                                handleChange,
                            }}
                        />
                    </Grid>
                    <Grid item xs>
                        <Fields.TypeToggleFields
                            hook={{
                                values,
                                errors,
                                handleChange,
                            }}
                            disabled={values['isAd']}
                            label={'Hide name?'}
                            name={'isNameHidden'}
                        />
                    </Grid>
                </Grid>

                {!(values['type'] === 'link' && values['isAd']) &&

                    <Fields.SimpleTextInput
                        hook={{
                            values,
                            handleChange,
                            errors,
                        }}
                        label="Header Name"
                        name="headerName"
                    />
                }
                {!(values['type'] === 'link' && values['isAd']) &&

                    <Fields.FieldTypeSelector
                        hook={{
                            values,
                            handleChange,
                            errors,
                        }}
                        label="Card background.."
                        name="cardBackground"
                        options={backgroundOptions}
                    />}


                {!values['isAd'] && values['cardBackground'] === 'custom' &&
                    <React.Fragment>
                        {((!values['isAd'] && values['cardBackground'] === 'custom') || values['isAd']) &&
                            <React.Fragment>
                                <Fields.FieldToggleSwitch
                                    hook={{
                                        errors,
                                        values,
                                        handleChange,
                                    }}
                                    label="Small size?"
                                    name="isSmallSize"
                                />
                            </React.Fragment>
                        }
                        <Fields.TypeImageField
                            hook={{
                                values,
                                errors,
                                handleChange,
                            }}
                            labels={[
                                {
                                    type: '.jpg, .png, .gif',
                                    additional: {
                                        file: '< 5mb',
                                        dimensions: 'Optimal: 1024px x 426px',
                                    },
                                    icon: 'photo',
                                },
                            ]}
                            name="cardImageURL"
                        />
                    </React.Fragment>
                }

            </React.Fragment>
        );
    };

    const ContentDetails = () => {
        return (
            <React.Fragment>
                <Typography align="left" className={classes.detailsHeadings} variant="h6">
                    Content
                </Typography>
                <Divider className={classes.divider}/>
                <Fields.FieldTypeSelector
                    hook={{
                        values,
                        handleChange,
                        errors,
                    }}
                    label="Content type"
                    name="type"
                    options={types}
                />
                {contentFields(values['type'])}
            </React.Fragment>
        );
    };
    return (
        <Grid container direction="column" justify="center" spacing={3}>
            {activeDiscover && (
                <form
                    className={classes.form}
                    encType="multipart/form-data"
                    id="Details-form"
                    noValidate
                    onSubmit={handleSubmit}
                >
                    <Grid item style={{marginBottom: '25px'}}>
                        {AppearanceDetails()}
                    </Grid>
                    <Grid item>{ContentDetails()}</Grid>
                    <Grid item>
                        <Button
                            className={classes.button}
                            color="secondary"
                            data-cy="Discover-details-update"
                            disabled={discoverCardStatus === 'saving'}
                            type="submit"
                            variant="contained"
                        >
                            update
                        </Button>
                    </Grid>
                </form>
            )}
        </Grid>
    );
};

export default DiscoverDetailsForm;
